import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Activity from './Activity';
import Folder from './Folder';
import Trash from './Trash';
import {
  ActivityLibraries,
  ActivityLibraryBlocks,
  FolderWithActivity,
  IFolderType,
} from '../../redux/activityLibrary/activityLibrarySlice';
import { ACTIVITY_LIBRARY_ROUTE } from '../../utils/routes';
import { ActivityTemplateType, selectActivityTemplate } from '../../redux/activity/activitySlice';
import { slimScrollbarStyles } from '../../utils/styles';
import { useAppSelector } from '../../redux/hooks';
import CustomNoRowsOverlay from '../Table/CustomNoRowsOverlay';
import NoResultsFound from './NoResultsFound';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';

interface IActivityLibraryProps {
  folders?: FolderWithActivity[];
  activityLibraries: ActivityLibraries[];
}

function ActivityLibrary({ folders, activityLibraries }: IActivityLibraryProps) {
  const location = useLocation();
  const [selectedActivity, setSelectedActivity] = useState<ActivityLibraries | null>(null);
  const dispatch = useDispatch();
  const currentBlock = useAppSelector((state) => state.activityLibrary.block);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  const handleActivityClick = (activity: ActivityLibraries) => {
    if (!activity) return;
    setSelectedActivity(activity);
    dispatch(
      selectActivityTemplate({
        type: ActivityTemplateType.ACTIVITY_LIBRARY,
        activity: {
          id: activity.id,
          name: activity.name,
          type: 'activity_library',
          objective: activity.objective,
          ActivityStep: activity.ActivityLibraryStep,
        },
      })
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.activity-wrapper') && !target.closest('button')) {
        setSelectedActivity(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (location.pathname === ACTIVITY_LIBRARY_ROUTE) {
    return (
      <Grid
        container
        spacing="20px"
        pb={location.pathname === ACTIVITY_LIBRARY_ROUTE ? '36px' : ''}
        height="70vh"
        sx={slimScrollbarStyles}
        overflow="auto"
        pr="10px"
        alignContent="flex-start"
      >
        {activityLibraries.length === 0 && folders?.length === 0 && <NoResultsFound />}
        {activityLibraries.map((activity) => (
          <Activity
            key={activity.id}
            activity={activity}
            isSelected={selectedActivity?.id === activity.id}
            onClick={() => handleActivityClick(activity)}
          />
        ))}
        {folders?.map((folder) => {
          if (folder.folderType === IFolderType.NORMAL) {
            return <Folder key={folder.id} folder={folder} />;
          }
          return (
            location.pathname === ACTIVITY_LIBRARY_ROUTE &&
            checkPermissions([
              UserPermissionType.ACTIVITY_LIBRARY_MODULE_DELETE_ACTIVITY_LIBRARY,
            ]) && <Trash key={folder.id} folder={folder} />
          );
        })}
      </Grid>
    );
  }

  return (
    <Grid
      container
      spacing="20px"
      pb={location.pathname === ACTIVITY_LIBRARY_ROUTE ? '36px' : ''}
      height={currentBlock === ActivityLibraryBlocks.SINGLE_FOLDER ? '50vh' : '45vh'}
      sx={slimScrollbarStyles}
      overflow="auto"
      width="100%"
    >
      {activityLibraries.map((activity) => (
        <Activity
          key={activity.id}
          activity={activity}
          isSelected={selectedActivity?.id === activity.id}
          onClick={() => handleActivityClick(activity)}
        />
      ))}
      {folders?.map((folder) => {
        if (folder.folderType === IFolderType.NORMAL) {
          return <Folder key={folder.id} folder={folder} />;
        }
        return (
          location.pathname === ACTIVITY_LIBRARY_ROUTE && <Trash key={folder.id} folder={folder} />
        );
      })}
    </Grid>
  );
}

export default ActivityLibrary;
